/* useFetchWithMainCondition.ts */

/* React */
import { useEffect, useRef } from "react";

/* React Redux */
import { useDispatch, useSelector } from "react-redux";

/* Interfaces */
import {
  BaseParams,
  FetchWithMainConditionProps,
} from "src/shared/interfaces/charts/FetchWithMainCondition";
import { RootState } from "src/redux/store";

export const useFetchWithMainCondition = <T>({
  mainCondition,
  debouncedValue,
  customCohortsState,
  fetchAction,
  buildParams,
}: FetchWithMainConditionProps<T>) => {
  const dispatch = useDispatch();
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const allowedCareSites = useSelector(
    (state: RootState) => state.auth.allowedCareSites
  );
  const careSiteRestrictionEnabled = useSelector(
    (state: RootState) =>
      state.settings.backendSettings.careSiteRestrictionEnabled
  );
  const backendSettingsStatus = useSelector(
    (state: RootState) => state.settings.backendSettings.status
  );

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (!mainCondition?.value) return;
    // Only fetch once backend settings are known; fail closed if they cannot be loaded.
    if (backendSettingsStatus !== "success") return;
    if (careSiteRestrictionEnabled && (allowedCareSites === null || allowedCareSites.length === 0)) return;

    debounceRef.current = setTimeout(() => {
      const currentCohort =
        customCohortsState.customCohortsByDisease[
          mainCondition.value!
        ];

      const selectedCareSites =
        (debouncedValue.careSites?.value as string[] | undefined)?.filter(
          (site: string) => (allowedCareSites ?? []).includes(site)
        );

      const baseParams: BaseParams = {
        mainCondition: mainCondition.value!,
        cohort: debouncedValue.cohorts.value,
        feature: debouncedValue.features?.value,
        timePeriod: debouncedValue.timePeriod,
        customCohortsStr: currentCohort?.customCohortsStr,
        careSiteNames: careSiteRestrictionEnabled
          ? selectedCareSites?.length
            ? selectedCareSites
            : allowedCareSites ?? undefined
          : undefined,
      };

      const finalParams = buildParams(baseParams);
      dispatch(fetchAction(finalParams));
    }, 120);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [
    dispatch,
    mainCondition?.value,
    debouncedValue,
    buildParams,
    customCohortsState.customCohortsByDisease,
    allowedCareSites,
    careSiteRestrictionEnabled,
    backendSettingsStatus,
  ]);
};
